/**
 * Report + report-note document schemas and paths.
 *
 * Reports are the org-wide human-triage surface revived from the parked
 * TA-Home "Reports" widget (see
 * https://github.com/CodeinPlace/cip/issues/3043). Each report is a unit of
 * work a staff member triages: it carries a priority, a status, an optional
 * per-row action, a threaded notes subcollection, and per-viewer "last seen"
 * markers used to derive unread-activity indicators.
 *
 * The inbound email agent is the primary producer of reports (every processed
 * email creates one — completed when auto-handled, pending when escalated), but
 * reports are a general triage primitive and may also be created manually.
 *
 * Base schemas use {@link FirestoreTimestampSchema} so the admin/ and client/
 * SDK modules can extend them with their own timestamp transforms.
 */
import {z} from 'zod';
import {FirestoreTimestampSchema} from '../types';
import type {UniqueStringId} from '../../types/branded';
import {getBucketDocPath} from './reportBucket';

// ============================================================================
// Branded ids
// ============================================================================

/**
 * Firestore document id of a report — the last segment of the path built by
 * {@link getOnPlatformReportDocumentPath}. Reports are bucket-scoped, so the id
 * alone does not address the document; a `courseId` bucket is also required.
 */
export type ReportId = UniqueStringId<'ReportId'>;

/**
 * Firestore document id of a report note — the last segment of the path built
 * by {@link getReportNoteDocumentPath}.
 */
export type ReportNoteId = UniqueStringId<'ReportNoteId'>;

// ============================================================================
// Enums
// ============================================================================

/**
 * Report priority tier. Mirrors the parked widget's four-tier scheme; lower
 * numbers are higher priority (P0 is most urgent, P3 least).
 */
export const ReportPrioritySchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
]);

/** Inferred type for {@link ReportPrioritySchema}. */
export type ReportPriority = z.infer<typeof ReportPrioritySchema>;

/**
 * Report lifecycle status. `pending` rows are active work (rendered at the top
 * of the triage table); `completed` rows sink to the bottom. Auto-handled
 * emails create reports directly in `completed`.
 */
export const ReportStatusSchema = z.enum(['pending', 'completed']);

/** Inferred type for {@link ReportStatusSchema}. */
export type ReportStatus = z.infer<typeof ReportStatusSchema>;

/**
 * What produced the report. Only the email agent creates reports today, always
 * via the admin SDK. Planned `/{courseId}/report` forum-post induction will add
 * a `'forum'` member when that path is built; a client "manual" create path was
 * removed (the triage page is read + edit only — see the `onPlatformReports`
 * rules and docs/email-agent-review/FIX_PLAN.md §1A), so no `'manual'` producer
 * exists.
 */
export const ReportSourceSchema = z.enum(['email_agent']);

/** Inferred type for {@link ReportSourceSchema}. */
export type ReportSource = z.infer<typeof ReportSourceSchema>;

// ============================================================================
// Report document
// ============================================================================

/**
 * Base schema for a report document — the document addressed by
 * {@link getOnPlatformReportDocumentPath}.
 *
 * `courseId` is denormalized so the per-course TA-Home view can run a filtered
 * query without a join; `null` means the report is org-wide only. `noteCount`
 * and `lastNoteAt` are denormalized from the notes subcollection so the triage
 * table can render the note badge and derive unread state without reading every
 * note.
 */
export const ReportDocumentSchema = z
  .object({
    /** Priority tier (P0–P3). */
    priority: ReportPrioritySchema,
    /** Lifecycle status. */
    status: ReportStatusSchema,
    /** What produced this report. */
    source: ReportSourceSchema,
    /** Short human-readable classification of the report (the "Action type" column). */
    actionType: z.string(),
    /**
     * Human-readable display label for {@link actionType}, denormalized (frozen)
     * from the inquiry-type taxonomy at classification time. The triage UI shows
     * this in place of the raw slug; it falls back to `actionType` when absent
     * (older reports) or when the slug no longer resolves. Frozen rather than
     * looked up live because course-scoped TAs cannot read the admin-only
     * taxonomy under the reports rules model, so a renamed type affects new
     * reports only. `null`/absent for manual reports or pre-taxonomy rows.
     */
    actionTypeLabel: z.string().nullable().optional(),
    /**
     * Human-readable subject for the triage row — the forum post's title for
     * forum-sourced reports (kept in sync when the post is retitled), or the
     * email subject for email-agent reports (frozen at the thread's first
     * subject; follow-ups don't retitle). `null`/absent only on rows created
     * before titles existed.
     */
    title: z.string().nullable().optional(),
    /** External link surfaced in the triage row. */
    link: z.object({
      /** Target URL (or `#` when there is nothing to link to yet). */
      href: z.string(),
      /** Button label. */
      label: z.string(),
    }),
    /**
     * Label for an optional per-row destructive action (e.g. "Remove"). When
     * omitted the action cell is hidden — the row has no row-level action.
     */
    actionLabel: z.string().optional(),
    /**
     * Course this report belongs to, denormalized for the per-course view.
     * `null` for org-wide reports with no course affiliation.
     */
    courseId: z.string().nullable(),
    /**
     * Id of the inbound email that produced this report
     * (`emails/{messageId}`), or `null` for manually-created reports.
     */
    linkedEmailId: z.string().nullable(),
    /**
     * Conversation id of the linked email's thread, denormalized so the triage
     * UI can open the whole conversation (and future auto-response can operate
     * on the full thread as context). `null` for manual reports or emails with
     * no thread. Optional because the converters validate on read and there is
     * no backfill: reports created before this field existed must stay
     * readable.
     */
    threadId: z.string().nullable().optional(),
    /** Number of notes in the notes subcollection (denormalized). */
    noteCount: z.number().int().nonnegative(),
    /** Server time of the most recent note, or `null` when there are none. */
    lastNoteAt: FirestoreTimestampSchema.nullable(),
    /** Server time the report was created. */
    createdAt: FirestoreTimestampSchema,
    /** Server time the report was last mutated (status/priority/notes). */
    updatedAt: FirestoreTimestampSchema,
  })
  .loose();

/** Inferred type for {@link ReportDocumentSchema}. */
export type ReportDocument = z.infer<typeof ReportDocumentSchema>;

// ============================================================================
// Report note document (subcollection)
// ============================================================================

/**
 * Base schema for a single note — the document addressed by
 * {@link getReportNoteDocumentPath}.
 *
 * Notes are append-only. The UI derives whether a note is "mine" by comparing
 * {@link ReportNoteDocument.authorUid} to the current user rather than storing
 * a viewer-relative flag.
 */
export const ReportNoteDocumentSchema = z
  .object({
    /** Uid of the note author. */
    authorUid: z.string(),
    /** Author's display name at authoring time. */
    authorName: z.string(),
    /** Note body (plain text). */
    text: z.string(),
    /** Server time the note was authored. */
    at: FirestoreTimestampSchema,
  })
  .loose();

/** Inferred type for {@link ReportNoteDocumentSchema}. */
export type ReportNoteDocument = z.infer<typeof ReportNoteDocumentSchema>;

// ============================================================================
// Per-viewer "last seen" marker (subcollection)
// ============================================================================

/**
 * Base schema for a per-viewer seen marker — the document addressed by
 * {@link getReportSeenDocumentPath} (the document id is the viewer's uid).
 *
 * Unread activity is derived by comparing {@link ReportSeenDocument.lastSeenAt}
 * against the report's `lastNoteAt`: a report has unread notes for a viewer
 * when `lastNoteAt > lastSeenAt` (or when no seen marker exists yet).
 */
export const ReportSeenDocumentSchema = z
  .object({
    /** Server time this viewer last opened the report's notes. */
    lastSeenAt: FirestoreTimestampSchema,
  })
  .loose();

/** Inferred type for {@link ReportSeenDocumentSchema}. */
export type ReportSeenDocument = z.infer<typeof ReportSeenDocumentSchema>;

// ============================================================================
// Paths
// ============================================================================
//
// Reports live under the top-level `reports` bucket collection — see
// `reportBucket` for `REPORTS_COLLECTION`, the bucket ids, and
// `getBucketDocPath`. A course bucket holds an `onPlatformReports`
// subcollection — the triage items (agent-generated one-per-email plus
// staff-created #3043 reports). A report's `courseId` field always equals the
// id of the course bucket it lives under (denormalized so collection-group
// queries can filter by course).

/** Subcollection (under a course bucket) holding on-platform triage reports. */
export const ON_PLATFORM_REPORTS_SUBCOLLECTION = 'onPlatformReports' as const;

/** Subcollection (under a report) holding its notes. */
export const REPORT_NOTES_COLLECTION = 'notes' as const;

/** Subcollection (under a report) holding per-viewer seen markers. */
export const REPORT_SEEN_COLLECTION = 'seen' as const;

/**
 * On-platform reports subcollection path for a course bucket.
 *
 * @param args.courseId - The course bucket id.
 * @returns Firestore collection path `reports/{courseId}/onPlatformReports`.
 */
export function getOnPlatformReportsCollectionPath({
  courseId,
}: {
  courseId: string;
}): string {
  return `${getBucketDocPath({bucketId: courseId})}/${ON_PLATFORM_REPORTS_SUBCOLLECTION}`;
}

/**
 * Single on-platform report document path.
 *
 * @param args.courseId - The course bucket id.
 * @param args.reportId - The report's Firestore id.
 * @returns Firestore doc path `reports/{courseId}/onPlatformReports/{reportId}`.
 */
export function getOnPlatformReportDocumentPath({
  courseId,
  reportId,
}: {
  courseId: string;
  reportId: string;
}): string {
  return `${getOnPlatformReportsCollectionPath({courseId})}/${reportId}`;
}

/**
 * Notes subcollection path for a report.
 *
 * @param args.courseId - The course bucket id.
 * @param args.reportId - The parent report's id.
 * @returns Firestore collection path
 *   `reports/{courseId}/onPlatformReports/{reportId}/notes`.
 */
export function getReportNotesCollectionPath({
  courseId,
  reportId,
}: {
  courseId: string;
  reportId: string;
}): string {
  return `${getOnPlatformReportDocumentPath({courseId, reportId})}/${REPORT_NOTES_COLLECTION}`;
}

/**
 * Single note document path.
 *
 * @param args.courseId - The course bucket id.
 * @param args.reportId - The parent report's id.
 * @param args.noteId - The note's Firestore id.
 * @returns Firestore doc path
 *   `reports/{courseId}/onPlatformReports/{reportId}/notes/{noteId}`.
 */
export function getReportNoteDocumentPath({
  courseId,
  reportId,
  noteId,
}: {
  courseId: string;
  reportId: string;
  noteId: string;
}): string {
  return `${getReportNotesCollectionPath({courseId, reportId})}/${noteId}`;
}

/**
 * Per-viewer seen-marker document path.
 *
 * @param args.courseId - The course bucket id.
 * @param args.reportId - The parent report's id.
 * @param args.uid - The viewer's uid (used as the document id).
 * @returns Firestore doc path
 *   `reports/{courseId}/onPlatformReports/{reportId}/seen/{uid}`.
 */
export function getReportSeenDocumentPath({
  courseId,
  reportId,
  uid,
}: {
  courseId: string;
  reportId: string;
  uid: string;
}): string {
  return `${getOnPlatformReportDocumentPath({courseId, reportId})}/${REPORT_SEEN_COLLECTION}/${uid}`;
}

/**
 * Derives whether a report has unread note activity for a viewer.
 *
 * A report is unread when it has at least one note (`lastNoteAtMs` is
 * non-null) and either the viewer has never opened it (`lastSeenAtMs` is null)
 * or the latest note is strictly newer than the last seen time. The comparison
 * is strict (`>`), so a viewer who has seen up to and including the latest note
 * is considered caught up.
 *
 * @param args.lastNoteAtMs - Epoch-ms of the report's most recent note, or
 *   `null` when the report has no notes (always returns `false`).
 * @param args.lastSeenAtMs - Epoch-ms the viewer last opened the report's
 *   notes, or `null` when the viewer has no seen marker (unread if any note
 *   exists).
 * @returns `true` when there is unread activity for the viewer.
 */
export function hasUnreadNotes({
  lastNoteAtMs,
  lastSeenAtMs,
}: {
  lastNoteAtMs: number | null;
  lastSeenAtMs: number | null;
}): boolean {
  if (lastNoteAtMs === null) {
    return false;
  }
  if (lastSeenAtMs === null) {
    return true;
  }
  return lastNoteAtMs > lastSeenAtMs;
}
